import type {
  ApiSwatidCompareEvent,
  ApiSwatidCompareSchedule,
  ApiSwatidEventsCompareResponse,
  ApiSwatidEvent,
  SwatidEventType,
} from '@/types/api';
import { SWATID_EVENT_LABELS, SWATID_SCHEDULE_MATCH_TOLERANCE_MIN } from '@/constants/swatid';
import {
  formatMadridDate,
  formatMadridTime,
  hasExplicitUtcOrOffset,
} from '@/utils/timezoneDisplay';

export type SwatidEventCategory = 'relay' | 'schedule' | 'mode' | 'other';

export type SwatidMismatchType =
  | 'missed_start'
  | 'missed_end'
  | 'unscheduled_activation'
  | 'late_start'
  | 'early_end';

export interface SwatidMismatch {
  type: SwatidMismatchType;
  relayNumber: number;
  scheduleStart?: string;
  scheduleEnd?: string;
  eventTime?: string;
  message: string;
}

export interface SwatidCompareAnalysis {
  mismatches: SwatidMismatch[];
  scheduledActivations: number;
  unscheduledActivations: number;
  matchedWindows: number;
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
}

function isRelayEventType(type: string): boolean {
  return type.startsWith('RELAY_COMMAND_');
}

export function getSwatidEventLabel(eventType: string): string {
  return SWATID_EVENT_LABELS[eventType] ?? eventType;
}

export function getSwatidEventCategory(eventType: string): SwatidEventCategory {
  if (isRelayEventType(eventType)) return 'relay';
  if (eventType.startsWith('SCHEDULE_')) return 'schedule';
  if (eventType === 'MODE_CHANGED') return 'mode';
  return 'other';
}

/** Indica si el evento parece manual (fuera de ventanas programadas) */
export function isLikelyManualRelayEvent(
  event: ApiSwatidCompareEvent,
  schedules: ApiSwatidCompareSchedule[],
  toleranceMin = SWATID_SCHEDULE_MATCH_TOLERANCE_MIN
): boolean {
  if (event.eventType !== 'RELAY_COMMAND_STARTED') return false;
  const eventMin = timeToMinutes(event.time);
  return !schedules.some((s) => {
    if (s.relayNumber !== event.relayNumber) return false;
    const start = timeToMinutes(s.startTime);
    const end = timeToMinutes(s.endTime);
    return eventMin >= start - toleranceMin && eventMin <= end + toleranceMin;
  });
}

export function analyzeEventsCompare(
  data: ApiSwatidEventsCompareResponse,
  toleranceMin = SWATID_SCHEDULE_MATCH_TOLERANCE_MIN
): SwatidCompareAnalysis {
  const mismatches: SwatidMismatch[] = [];
  let matchedWindows = 0;
  let unscheduledActivations = 0;

  const relayEvents = data.events.filter((e) => isRelayEventType(e.eventType));

  for (const schedule of data.schedules) {
    const starts = relayEvents.filter(
      (e) =>
        e.relayNumber === schedule.relayNumber &&
        e.eventType === 'RELAY_COMMAND_STARTED'
    );
    const ends = relayEvents.filter(
      (e) =>
        e.relayNumber === schedule.relayNumber &&
        (e.eventType === 'RELAY_COMMAND_COMPLETED' ||
          e.eventType === 'RELAY_COMMAND_CANCELLED')
    );

    const scheduleStartMin = timeToMinutes(schedule.startTime);
    const scheduleEndMin = timeToMinutes(schedule.endTime);

    const matchedStart = starts.find((e) => {
      const diff = Math.abs(timeToMinutes(e.time) - scheduleStartMin);
      return diff <= toleranceMin;
    });

    const matchedEnd = ends.find((e) => {
      const t = timeToMinutes(e.time);
      return t >= scheduleStartMin && Math.abs(t - scheduleEndMin) <= toleranceMin;
    });

    if (matchedStart && matchedEnd) {
      matchedWindows += 1;
    } else {
      if (!matchedStart) {
        mismatches.push({
          type: 'missed_start',
          relayNumber: schedule.relayNumber,
          scheduleStart: schedule.startTime,
          scheduleEnd: schedule.endTime,
          message: `Relé ${schedule.relayNumber}: no se detectó encendido programado (~${schedule.startTime})`,
        });
      }
      if (!matchedEnd && matchedStart) {
        mismatches.push({
          type: 'missed_end',
          relayNumber: schedule.relayNumber,
          scheduleStart: schedule.startTime,
          scheduleEnd: schedule.endTime,
          message: `Relé ${schedule.relayNumber}: no se detectó apagado programado (~${schedule.endTime})`,
        });
      }
      if (matchedStart && timeToMinutes(matchedStart.time) - scheduleStartMin > toleranceMin) {
        mismatches.push({
          type: 'late_start',
          relayNumber: schedule.relayNumber,
          scheduleStart: schedule.startTime,
          eventTime: matchedStart.time,
          message: `Relé ${schedule.relayNumber}: encendido tardío (${matchedStart.time} vs ${schedule.startTime})`,
        });
      }
      if (matchedEnd && scheduleEndMin - timeToMinutes(matchedEnd.time) > toleranceMin) {
        mismatches.push({
          type: 'early_end',
          relayNumber: schedule.relayNumber,
          scheduleEnd: schedule.endTime,
          eventTime: matchedEnd.time,
          message: `Relé ${schedule.relayNumber}: apagado anticipado (${matchedEnd.time} vs ${schedule.endTime})`,
        });
      }
    }
  }

  for (const event of relayEvents) {
    if (
      event.eventType === 'RELAY_COMMAND_STARTED' &&
      isLikelyManualRelayEvent(event, data.schedules, toleranceMin)
    ) {
      unscheduledActivations += 1;
      mismatches.push({
        type: 'unscheduled_activation',
        relayNumber: event.relayNumber,
        eventTime: event.time,
        message: `Relé ${event.relayNumber}: activación a las ${event.time} sin programación coincidente (posible acción manual o remota)`,
      });
    }
  }

  return {
    mismatches,
    scheduledActivations: data.schedulesCount,
    unscheduledActivations,
    matchedWindows,
  };
}

export function formatSwatidEventTime(event: ApiSwatidEvent): string {
  const raw = event.deviceTimestamp || event.createdAt;
  try {
    // deviceTimestamp suele venir como reloj dispositivo (España) sin Z — no reconvertir
    if (!hasExplicitUtcOrOffset(raw) && /\d{4}-\d{2}-\d{2}/.test(raw)) {
      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const timePart = (normalized.split('T')[1] ?? '').slice(0, 8);
      const time =
        timePart.length >= 8 ? timePart : `${formatMadridTime(normalized)}:00`;
      return `${formatMadridDate(normalized)}, ${time}`;
    }
    // UTC con Z: una sola conversión a Europe/Madrid
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return new Intl.DateTimeFormat('es-ES', {
        timeZone: 'Europe/Madrid',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(d);
    }
    return `${formatMadridDate(raw)}, ${formatMadridTime(raw)}`;
  } catch {
    return raw;
  }
}

export function describeRelayEventAction(
  eventType: SwatidEventType | string,
  eventData?: Record<string, unknown>
): string {
  if (eventType === 'RELAY_COMMAND_STARTED') {
    const duration = eventData?.duration;
    if (typeof duration === 'number' && duration > 0) {
      return `ON (${duration}s)`;
    }
    return 'ON';
  }
  if (eventType === 'RELAY_COMMAND_COMPLETED') return 'OFF';
  if (eventType === 'RELAY_COMMAND_CANCELLED') return 'Cancelado';
  return getSwatidEventLabel(eventType);
}

export function getEventDateIso(event: ApiSwatidEvent): string {
  const raw = event.deviceTimestamp || event.createdAt;
  try {
    // Reloj de pared sin Z → fecha civil del string
    if (!hasExplicitUtcOrOffset(raw) && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return raw.slice(0, 10);
    }
    // UTC → fecha civil Europe/Madrid
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Madrid',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return raw.slice(0, 10);
  }
}

export function filterEventsByDate(events: ApiSwatidEvent[], date: string): ApiSwatidEvent[] {
  if (!date) return events;
  return events.filter((e) => getEventDateIso(e) === date);
}

export type TimelineRowStatus = 'ok' | 'warning' | 'error' | 'idle';

export interface SwatidTimelineScheduleBar {
  startTime: string;
  endTime: string;
  startPercent: number;
  widthPercent: number;
}

export interface SwatidTimelineEventMarker {
  time: string;
  timePercent: number;
  eventType: string;
  label: string;
  manual: boolean;
  isStart: boolean;
  isEnd: boolean;
}

export interface SwatidTimelineRow {
  relayNumber: number;
  relayLabel: string;
  schedules: SwatidTimelineScheduleBar[];
  events: SwatidTimelineEventMarker[];
  status: TimelineRowStatus;
  mismatchCount: number;
}

const MINUTES_PER_DAY = 24 * 60;

export function minutesToDayPercent(minutes: number): number {
  return Math.min(100, Math.max(0, (minutes / MINUTES_PER_DAY) * 100));
}

export function buildTimelineRows(
  data: ApiSwatidEventsCompareResponse,
  relayLabels: Map<number, string>,
  toleranceMin = SWATID_SCHEDULE_MATCH_TOLERANCE_MIN
): SwatidTimelineRow[] {
  const analysis = analyzeEventsCompare(data, toleranceMin);
  const mismatchByRelay = new Map<number, number>();
  for (const m of analysis.mismatches) {
    mismatchByRelay.set(m.relayNumber, (mismatchByRelay.get(m.relayNumber) ?? 0) + 1);
  }

  const relayNumbers = new Set<number>();
  data.schedules.forEach((s) => relayNumbers.add(s.relayNumber));
  data.events.forEach((e) => relayNumbers.add(e.relayNumber));

  const sortedRelays = [...relayNumbers].sort((a, b) => a - b);
  if (sortedRelays.length === 0) return [];

  return sortedRelays.map((relayNumber) => {
    const schedules = data.schedules
      .filter((s) => s.relayNumber === relayNumber)
      .map((s) => {
        const startMin = timeToMinutes(s.startTime);
        const endMin = timeToMinutes(s.endTime);
        const startPercent = minutesToDayPercent(startMin);
        const widthPercent = Math.max(0.5, minutesToDayPercent(endMin) - startPercent);
        return {
          startTime: s.startTime,
          endTime: s.endTime,
          startPercent,
          widthPercent,
        };
      });

    const relaySchedules = data.schedules.filter((s) => s.relayNumber === relayNumber);
    const events = data.events
      .filter((e) => e.relayNumber === relayNumber)
      .map((e) => {
        const timeMin = timeToMinutes(e.time);
        const manual =
          e.eventType === 'RELAY_COMMAND_STARTED' &&
          isLikelyManualRelayEvent(e, relaySchedules, toleranceMin);
        return {
          time: e.time,
          timePercent: minutesToDayPercent(timeMin),
          eventType: e.eventType,
          label: getSwatidEventLabel(e.eventType),
          manual,
          isStart: e.eventType === 'RELAY_COMMAND_STARTED',
          isEnd:
            e.eventType === 'RELAY_COMMAND_COMPLETED' ||
            e.eventType === 'RELAY_COMMAND_CANCELLED',
        };
      });

    const mismatchCount = mismatchByRelay.get(relayNumber) ?? 0;
    let status: TimelineRowStatus = 'idle';
    if (schedules.length > 0 || events.length > 0) {
      status = mismatchCount > 0 ? 'error' : events.length > 0 ? 'ok' : 'warning';
    }

    return {
      relayNumber,
      relayLabel: relayLabels.get(relayNumber) ?? `Relé ${relayNumber}`,
      schedules,
      events,
      status,
      mismatchCount,
    };
  });
}

export function formatHourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}
